import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/achc-accreditation-guide#article`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/achc-accreditation-guide`,
  headline: 'The Complete Guide to ACHC Accreditation for Home Health Agencies',
  description: 'Everything your home health agency needs to know about achieving ACHC accreditation — from initial readiness assessments to final survey preparation and beyond.',
  image: 'https://public.readdy.ai/ai/img_res/4ab196befab5103c508ac9d0b4a7b731.jpg',
  datePublished: '2026-03-15',
  dateModified: '2026-03-15',
  author: {
    '@type': 'Person',
    name: 'Dr. Osmel Villarreal',
    jobTitle: 'ACHC Certified Consultant, DBA, MBA, MS',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/why-dr-villarreal`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Advantixx Home Health Accreditation Consultants',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  },
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog#webpage` },
  keywords: ['ACHC accreditation', 'home health agency', 'accreditation guide', 'CMS deeming authority', 'compliance'],
  articleSection: 'Accreditation',
  wordCount: 1800,
};

export default function AchcAccreditationGuide() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-achc-guide';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-achc-guide');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-0">
        <div className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20healthcare%20consultant%20reviewing%20compliance%20documents%20in%20a%20modern%20office%20with%20gold%20accents%2C%20clean%20minimalist%20workspace%2C%20warm%20lighting%2C%20high%20quality&width=1400&height=600&seq=blog1hero&orientation=landscape"
            alt="ACHC Accreditation Guide for Home Health Agencies"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/50 to-[#0d0d0d]/20"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 md:px-10 pb-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-[#d4af37] text-[#0d0d0d] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Accreditation</span>
              <span className="text-[#888] text-xs">March 15, 2026</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">8 min read</span>
              <span className="text-[#888] text-xs">•</span>
              <span className="text-[#888] text-xs">By Dr. Osmel Villarreal, DBA</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight">
              The Complete Guide to ACHC Accreditation<br className="hidden md:block" />
              <span className="text-[#d4af37]"> for Home Health Agencies</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content */}
          <article className="flex-1 min-w-0">
            {/* Intro */}
            <p className="text-[#bbb] text-lg leading-relaxed mb-8 border-l-4 border-[#d4af37] pl-6">
              Achieving ACHC accreditation is one of the most significant milestones a home health agency can reach. It signals to patients, payers, and regulators that your agency meets the highest standards of quality and compliance. But the path to accreditation can be complex — and costly if you go in unprepared.
            </p>

            <p className="text-[#999] leading-relaxed mb-8">
              This comprehensive guide walks you through every phase of the ACHC accreditation process, from understanding what ACHC is and why it matters, to the specific steps your agency must take to achieve and maintain accreditation successfully.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-black text-white mb-5 mt-12">
              <span className="text-[#d4af37]">01.</span> What Is ACHC Accreditation?
            </h2>
            <p className="text-[#999] leading-relaxed mb-5">
              The Accreditation Commission for Health Care (ACHC) is a nationally recognized accreditation organization with CMS deeming authority. This means that ACHC-accredited agencies are deemed to meet Medicare Conditions of Participation (CoPs) without undergoing a separate state survey.
            </p>
            <p className="text-[#999] leading-relaxed mb-8">
              ACHC sets rigorous standards for quality and compliance across home health, hospice, pharmacy, and other healthcare services. For home health agencies, ACHC accreditation is not just a credential — it is a competitive advantage that opens doors to more payer contracts, builds patient trust, and demonstrates operational excellence.
            </p>

            {/* Key Benefits Box */}
            <div className="bg-[#111] border border-[#d4af37]/20 rounded-xl p-7 mb-10">
              <h3 className="text-[#d4af37] font-bold text-sm uppercase tracking-widest mb-5">Key Benefits of ACHC Accreditation</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', text: 'CMS deeming authority — no separate Medicare survey' },
                  { icon: 'ri-trophy-line', text: 'Competitive advantage in your market' },
                  { icon: 'ri-links-line', text: 'Access to more payer contracts and referrals' },
                  { icon: 'ri-heart-pulse-line', text: 'Improved patient outcomes and care quality' },
                  { icon: 'ri-team-line', text: 'Enhanced staff accountability and performance' },
                  { icon: 'ri-bar-chart-line', text: 'Foundation for long-term agency growth' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                      <i className={`${item.icon} text-[#d4af37] text-sm`}></i>
                    </div>
                    <span className="text-[#aaa] text-sm leading-relaxed">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-black text-white mb-5 mt-12">
              <span className="text-[#d4af37]">02.</span> The ACHC Accreditation Process: Step by Step
            </h2>
            <p className="text-[#999] leading-relaxed mb-8">
              The ACHC accreditation process follows a structured pathway. Understanding each phase helps your agency prepare effectively and avoid common pitfalls that delay accreditation.
            </p>

            {[
              { step: 'Step 1', title: 'Initial Readiness Assessment', desc: 'Before applying, conduct a thorough gap analysis of your current operations against ACHC standards. Identify deficiencies in policies, procedures, documentation, and staff training. This assessment forms the foundation of your accreditation roadmap.' },
              { step: 'Step 2', title: 'Application & Agreement', desc: 'Submit your application to ACHC and sign the accreditation agreement. ACHC will assign a dedicated account advisor to guide you through the process. You will receive access to ACHC standards and educational resources.' },
              { step: 'Step 3', title: 'Policy & Procedure Development', desc: 'Develop or update all required policies and procedures to align with ACHC standards. This includes clinical policies, administrative procedures, HR policies, infection control protocols, and emergency preparedness plans.' },
              { step: 'Step 4', title: 'Staff Training & Education', desc: 'Train all staff on ACHC standards, your agency\'s policies, and their specific roles in maintaining compliance. Document all training activities — ACHC surveyors will review training records during the survey.' },
              { step: 'Step 5', title: 'Mock Survey', desc: 'Conduct a comprehensive mock survey to simulate the actual ACHC survey experience. Identify remaining gaps, correct deficiencies, and ensure all staff are prepared to respond to surveyor questions confidently.' },
              { step: 'Step 6', title: 'ACHC On-Site Survey', desc: 'ACHC surveyors will conduct an on-site visit to evaluate your agency\'s compliance with all applicable standards. They will review documentation, interview staff, observe operations, and assess patient records.' },
              { step: 'Step 7', title: 'Accreditation Decision & Ongoing Compliance', desc: 'After the survey, ACHC issues an accreditation decision. If deficiencies are found, you will submit a Plan of Correction. Once accredited, maintain continuous compliance through regular internal audits and staff education.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-5 mb-7">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
                    <span className="text-[#d4af37] text-xs font-black">{i + 1}</span>
                  </div>
                </div>
                <div>
                  <span className="text-[#d4af37] text-xs font-semibold uppercase tracking-wide">{item.step}</span>
                  <h3 className="text-white font-bold text-base mb-2">{item.title}</h3>
                  <p className="text-[#888] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}

            {/* Section 3 */}
            <h2 className="text-2xl font-black text-white mb-5 mt-12">
              <span className="text-[#d4af37]">03.</span> Common Mistakes That Delay Accreditation
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {[
                'Incomplete or outdated policies and procedures',
                'Insufficient staff training documentation',
                'Poor clinical record organization',
                'Lack of a quality assurance program',
                'Inadequate emergency preparedness planning',
                'Failure to conduct internal audits regularly',
                'Inconsistent infection control practices',
                'Missing or incomplete personnel files',
              ].map((mistake, i) => (
                <div key={i} className="flex items-start gap-3 bg-[#111] rounded-lg p-4 border border-[#1e1e1e]">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-close-circle-line text-red-400 text-sm"></i>
                  </div>
                  <span className="text-[#888] text-sm leading-relaxed">{mistake}</span>
                </div>
              ))}
            </div>

            {/* Section 4 */}
            <h2 className="text-2xl font-black text-white mb-5 mt-12">
              <span className="text-[#d4af37]">04.</span> How Advantixx Simplifies the Process
            </h2>
            <p className="text-[#999] leading-relaxed mb-6">
              At Advantixx, we have guided numerous home health agencies through the ACHC accreditation process. Our ACHC Certified Consultant, Dr. Osmel Villarreal, DBA, brings doctoral-level research expertise and real-world agency management experience to every engagement.
            </p>
            <p className="text-[#999] leading-relaxed mb-8">
              We handle the complexity so you can focus on patient care. From initial readiness assessments and policy development to mock surveys and ongoing compliance support, Advantixx provides a complete, end-to-end accreditation solution tailored to your agency&apos;s specific needs.
            </p>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-8 text-center mt-12">
              <h3 className="text-white font-black text-xl mb-3">Ready to Start Your ACHC Accreditation?</h3>
              <p className="text-[#888] text-sm mb-6 leading-relaxed">
                Book a free consultation with Dr. Villarreal and get a personalized accreditation roadmap for your agency.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Book a Free Consultation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="sticky top-28 flex flex-col gap-6">
              {/* Author */}
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">About the Author</p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-[#d4af37] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Dr. Osmel Villarreal</p>
                    <p className="text-[#666] text-xs">DBA, MBA, MS</p>
                  </div>
                </div>
                <p className="text-[#777] text-xs leading-relaxed">
                  ACHC Certified Consultant and healthcare entrepreneur with doctoral research expertise in home health operations and compliance.
                </p>
                <Link
                  to="/why-dr-villarreal"
                  className="mt-4 flex items-center gap-1.5 text-[#d4af37] text-xs font-semibold hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Learn More <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              {/* Related Posts */}
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Related Articles</p>
                <div className="flex flex-col gap-5">
                  {[
                    { title: 'Top 10 Compliance Tips for 2026', path: '/blog/home-health-compliance-tips' },
                    { title: 'How to Prepare for a Mock Survey', path: '/blog/mock-survey-preparation' },
                    { title: 'Policy & Procedure Development Guide', path: '/blog/mock-survey-preparation' },
                  ].map((post, i) => (
                    <Link key={i} to={post.path} className="group cursor-pointer">
                      <p className="text-[#aaa] text-sm leading-snug group-hover:text-[#d4af37] transition-colors duration-200">
                        {post.title}
                      </p>
                      <span className="text-[#555] text-xs mt-1 flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                        Read <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="bg-[#111] border border-[#1e1e1e] rounded-xl p-6">
                <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-4">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {['ACHC', 'Accreditation', 'Home Health', 'Compliance', 'CMS', 'Survey Prep', 'Policy'].map((tag) => (
                    <span key={tag} className="text-[#666] text-xs border border-[#2a2a2a] px-3 py-1.5 rounded-full hover:border-[#d4af37]/40 hover:text-[#d4af37] transition-colors duration-200 cursor-pointer">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-[#d4af37]/15 to-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-6 text-center">
                <i className="ri-calendar-check-line text-[#d4af37] text-3xl mb-3 block"></i>
                <p className="text-white font-bold text-sm mb-2">Free Consultation</p>
                <p className="text-[#777] text-xs mb-4 leading-relaxed">Get your personalized ACHC accreditation roadmap today.</p>
                <Link
                  to="/services"
                  className="block bg-[#d4af37] text-[#0d0d0d] font-bold text-xs px-5 py-2.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Back to Blog */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[#888] hover:text-[#d4af37] text-sm transition-colors duration-200 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Back to All Articles
        </Link>
      </div>

      <Footer />
    </div>
  );
}
